import React from 'react';
import { Card, CardContent, CardHeader, Divider, Grid, List, ListItem, Skeleton, Stack } from '@mui/material';

/**
 * A vertical stack of text skeleton rows.
 * @param {string[]} widths - Array of widths for each row (e.g. ['40%', '55%', '35%'])
 */
export const SkeletonDetailRows = ({ widths = ['40%', '55%', '35%', '50%'] }) => (
  <Stack spacing={1}>
    {widths.map((w, i) => (
      <Skeleton key={i} variant="text" width={w} />
    ))}
  </Stack>
);

/**
 * An outlined Card with a skeleton title in the CardHeader, a divider, and
 * either custom children or a SkeletonDetailRows in the CardContent.
 * Matches the Panel component's visual structure.
 * @param {number} titleWidth - Width of the title skeleton
 * @param {number} titleHeight - Height of the title skeleton
 * @param {string[]} rowWidths - Widths for SkeletonDetailRows (used when no children)
 * @param {object} cardProps - Extra props spread onto the Card
 * @param {React.ReactNode} children - Custom content for CardContent (overrides rowWidths)
 */
export const SkeletonPanelCard = ({
  titleWidth = 180,
  titleHeight = 28,
  rowWidths,
  cardProps,
  children,
}) => (
  <Card variant="outlined" {...cardProps}>
    <CardHeader
      title={<Skeleton variant="text" width={titleWidth} height={titleHeight} />}
      sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
    />
    <CardContent sx={children ? { p: 0 } : undefined}>
      {children ?? <SkeletonDetailRows widths={rowWidths} />}
    </CardContent>
  </Card>
);

/**
 * A Card containing a List with a fixed number of skeleton items separated by Dividers.
 * @param {number} count - Number of list items to render
 * @param {object} cardProps - Extra props spread onto the Card
 * @param {object} listItemSx - sx prop applied to each ListItem
 * @param {function} renderItem - (index: number) => ReactNode — renders each item's content
 */
export const SkeletonListCard = ({ count = 3, cardProps, listItemSx, renderItem }) => (
  <Card {...cardProps}>
    <List disablePadding>
      {Array.from({ length: count }).map((_, i) => (
        <React.Fragment key={i}>
          <ListItem sx={listItemSx}>
            {renderItem(i)}
          </ListItem>
          {i < count - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  </Card>
);

/**
 * A single equipment row: optional image placeholder on the left, name + status chip
 * + tags + detail rows on the right. Matches the equipment list item layout.
 * @param {boolean} hideEquipmentImage - When true, the image column is hidden
 */
export const SkeletonEquipmentItem = ({ hideEquipmentImage = false }) => (
  <Grid container sx={{ flexWrap: { xs: 'wrap', lg: 'nowrap' }, width: '100%' }}>
    {!hideEquipmentImage && (
      <Grid item xs={12} lg="auto" sx={{ mr: { lg: 4 }, mb: { xs: 2, lg: 0 } }}>
        <Skeleton
          variant="rectangular"
          sx={{ width: { xs: '100%', lg: '10rem' }, height: 120, borderRadius: 1 }}
        />
      </Grid>
    )}
    <Grid item md={hideEquipmentImage ? 8 : 7} sm={6} xs={12}>
      <Stack spacing={3}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Stack>
        <Stack direction="row" gap={1}>
          <Skeleton variant="rounded" width={60} height={20} />
          <Skeleton variant="rounded" width={70} height={20} />
        </Stack>
        <SkeletonDetailRows widths={['35%', '30%', '45%']} />
      </Stack>
    </Grid>
  </Grid>
);
